import { Dispatch } from 'redux';
import { getExpressCheckoutConditions as getExpressCheckoutConditionsApi } from '../api/expressCheckoutConditions';
import { ExpressCheckoutConditionsApiResponse } from '../models/expressCheckout';
import { PaymentConstraintName } from '../models/paymentMethods/enums';
import { getEventId } from '../selectors/event';
import { areSyncExpressCheckoutEligibleCommonRulesMet } from '../selectors/expressCheckoutConditions';
import { CheckoutState } from '../state';
import { ExpressCheckoutConditions } from '../state/expressCheckout';
import { updateStatsigUserMetadata } from './statsig';

export enum ExpressCheckoutConditionsActionType {
    saveExpressCheckoutConditions = 'saveExpressCheckoutConditions',
}

export interface SaveExpressCheckoutConditionsAction {
    type: ExpressCheckoutConditionsActionType.saveExpressCheckoutConditions;
    payload: ExpressCheckoutConditions;
}

export const saveExpressCheckoutConditionsAction = (
    expressCheckoutConditions: ExpressCheckoutConditions,
): SaveExpressCheckoutConditionsAction => ({
    type: ExpressCheckoutConditionsActionType.saveExpressCheckoutConditions,
    payload: expressCheckoutConditions,
});

const hasPaymentMethodInPaymentOptions = (
    paymentMethod: PaymentConstraintName,
    paymentOptions?: ExpressCheckoutConditionsApiResponse['payment_options'],
) =>
    !!paymentOptions?.find(({ instrument_types }) =>
        instrument_types.find(({ variants }) =>
            variants.includes(paymentMethod),
        ),
    );

export const getHasApplePayInPaymentOptions = (
    paymentOptions?: ExpressCheckoutConditionsApiResponse['payment_options'],
): boolean =>
    hasPaymentMethodInPaymentOptions(
        PaymentConstraintName.BRAINTREE_APPLE_PAY,
        paymentOptions,
    );

export const getHasPaypalInPaymentOptions = (
    paymentOptions?: ExpressCheckoutConditionsApiResponse['payment_options'],
): boolean =>
    hasPaymentMethodInPaymentOptions(
        PaymentConstraintName.BRAINTREE_PAYPAL,
        paymentOptions,
    );

export const getHasGooglePayInPaymentOptions = (
    paymentOptions?: ExpressCheckoutConditionsApiResponse['payment_options'],
): boolean =>
    hasPaymentMethodInPaymentOptions(
        PaymentConstraintName.BRAINTREE_GOOGLE_PAY,
        paymentOptions,
    );

export const getExpressCheckoutConditions =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const eventId = getEventId(getState());

        if (!areSyncExpressCheckoutEligibleCommonRulesMet(getState())) {
            return null;
        }

        const { response } = await getExpressCheckoutConditionsApi(eventId);

        await dispatch(
            saveExpressCheckoutConditionsAction({
                hasApplePay: getHasApplePayInPaymentOptions(
                    response.payment_options,
                ),
                hasCannedQuestions: !!response.has_canned_questions,
                hasCustomQuestions: !!response.has_custom_questions,
                hasPrepaymentQuestions: !!response.has_prepayment_questions,
                hasPaypal: getHasPaypalInPaymentOptions(
                    response.payment_options,
                ),
                hasGooglePay: getHasGooglePayInPaymentOptions(
                    response.payment_options,
                ),
                hasRequiredCannedQuestions:
                    !!response.has_required_canned_questions,
                hasRequiredCustomQuestions:
                    !!response.has_required_custom_questions,
            }),
        );

        dispatch(updateStatsigUserMetadata);
    };
